/**
 * BottomSheet Component
 *
 * Portal-rendered bottom sheet with overlay, slide-up animation,
 * swipe-to-dismiss, and iOS-safe body scroll lock.
 *
 * Uses bg-op-card for dark mode support. Renders into document.body
 * via createPortal to escape parent z-index contexts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface BottomSheetProps {
  /** Controls visibility */
  isOpen: boolean;
  /** Called when the sheet should close (overlay tap, swipe down, X button) */
  onClose: () => void;
  /** Optional title displayed at the top of the sheet */
  title?: string;
  /** Sheet content */
  children: React.ReactNode;
  /** Snap point height */
  snapTo?: 'half' | 'full';
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  snapTo = 'half',
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // dragOffset: pixels of drag displacement (0 = not dragging)
  const [dragOffset, setDragOffset] = useState(0);
  const dragOffsetRef = useRef(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isShown, setIsShown] = useState(false);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const scrollPositionRef = useRef(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Slide-up animation on open
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsShown(false);
      // Use requestAnimationFrame to ensure the off-screen position is painted
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
          setIsShown(true);
        });
      });
    } else {
      setIsAnimating(true);
      setIsShown(false);
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // iOS-safe body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    scrollPositionRef.current = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    };
  }, [isOpen]);

  // Swipe-to-dismiss with passive: false touch handlers
  useEffect(() => {
    if (!isOpen) return;

    const sheetEl = sheetRef.current;
    if (!sheetEl) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
      isDragging.current = false;
      setIsAnimating(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const deltaY = e.touches[0].clientY - touchStartY.current;
      const scrollTop = contentRef.current?.scrollTop ?? 0;

      // Only activate swipe-to-dismiss when at top of scroll content
      // and swiping downward
      if (deltaY > 0 && scrollTop <= 0) {
        isDragging.current = true;
        e.preventDefault();
        dragOffsetRef.current = deltaY;
        setDragOffset(deltaY);
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging.current) return;

      const currentOffset = dragOffsetRef.current;
      setIsAnimating(true);
      dragOffsetRef.current = 0;
      setDragOffset(0);

      if (currentOffset > 100) {
        onCloseRef.current();
      }
      isDragging.current = false;
    };

    sheetEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    sheetEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    sheetEl.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      sheetEl.removeEventListener('touchstart', handleTouchStart);
      sheetEl.removeEventListener('touchmove', handleTouchMove);
      sheetEl.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  if (!isVisible && !isOpen) return null;

  const heightClass = snapTo === 'full' ? 'max-h-[85vh]' : 'max-h-[50vh]';

  const sheet = (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm',
          isAnimating ? 'transition-opacity duration-300' : '',
          isShown ? 'opacity-100' : 'opacity-0',
        )}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Dialog'}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[60]',
          'bg-op-card rounded-t-3xl shadow-2xl',
          heightClass,
          'flex flex-col',
          isAnimating ? 'transition-transform duration-300 ease-out' : '',
        )}
        style={{
          transform: dragOffset > 0
            ? `translateY(${dragOffset}px)`
            : isShown
              ? 'translateY(0)'
              : 'translateY(100%)',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-2 pb-3">
          {title ? (
            <h2 className="text-lg font-semibold text-op-text-primary">{title}</h2>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-xl focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2 active:bg-gray-100 transition-colors duration-150"
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.5} className="text-op-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto px-4 pb-6">
          {children}
        </div>
      </div>
    </>
  );

  return createPortal(sheet, document.body);
}
